import React from 'react';

import { useTranslation } from 'react-i18next';

import { M } from 'components/typography';
import { PopupMenu, TextButton, Column } from 'design-system-web';
import { ProductCategory } from 'graphql-query-contracts';
import { useGetActionsForCategories } from './useGetActionsForCategories';
import { useBillingAndPaymentsServicesCRUDEnabled } from 'react-hooks';

interface TableColumn {
  title: string;
  dataIndex: string;
  key: string;
}

export function useGetCategoriesTableColumns(onDelete: (category: ProductCategory) => void): TableColumn[] {
  const { t } = useTranslation();
  const categoryActions = useGetActionsForCategories({ onDelete });
  const isBillingAndPaymentsServicesCRUDEnabled = useBillingAndPaymentsServicesCRUDEnabled();
  const categoryTableColumns: any = [
    {
      key: 'name',
      header: t('abp.categoryList.categoryName'),
      renderCell: renderStringCell,
    },
    {
      key: 'productType',
      header: t('abp.categoryList.productType'),
      renderCell: (cell: string) => renderProductTypeCell(cell, t),
    },
    {
      key: 'description',
      header: t('abp.categoryList.description'),
      renderCell: renderStringCell,
    },
  ];

  const actionColumn: Column<ProductCategory> = {
    key: 'actions',
    header: t('abp.actions'),
    renderCell: (_cell: boolean, row: ProductCategory) => {
      return (
        <PopupMenu
          trigger={
            <TextButton iconName="angle-down">{t('abp.actions')}</TextButton>
          }
          items={categoryActions.map(({ label, onSelect }) => ({
            label,
            onSelect: () => {
              onSelect(row);
            },
            isDisabled: false,
          }))}
        />
      );
    },
  };

  if (isBillingAndPaymentsServicesCRUDEnabled) {
    categoryTableColumns.push(actionColumn);
  }

  return categoryTableColumns;
}

function renderStringCell(cell: string) {
  return renderReactString(cell);
}

function renderProductTypeCell(
  cell: string,
  t: (key: string, opts?: any) => string
) {
  const productTypeWithoutPrefix = cell
    .replace('PRODUCT_TYPE_SERVICE_', '')
    .toLowerCase();

  const translatedString = t(
    `abp.productsServices.createCategory.productServiceType.${productTypeWithoutPrefix}`
  );

  return renderReactString(translatedString);
}

function renderReactString(stringToRender: string) {
  return <M>{stringToRender}</M>;
}
